import { useQuery } from "@tanstack/react-query";
import {
  ChangeRoomStatus,
  GetMessages,
  GetTickets,
  SendMessages,
  UserInfo,
} from "../../services/services";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useState, useEffect, useRef } from "react";
import queryString from "query-string";
import useSyncPageSize from "../../hook/useSyncPageSize";
import ResponsiveDataList from "../../components/ResponsiveDataList/ResponsiveDataList";
import PersianDate from "../../components/PersianDate/PersianDate";
import { Toast } from "primereact/toast";
import TextField from "../../components/Textfield/TextField";
import { SelectCustom } from "../../components/SelectCustom/SelectCustom";
import CloseRedIconSvg from "../../svg/CloseRedIconSvg";

const MESSAGES_PAGE_SIZE = 10;

const Tiket = () => {
  const pageSize = 15;
  const location = useLocation();
  const toast = useRef(null);

  const getFiltersFromUrl = () => {
    const parsed = queryString.parse(location.search);
    return {
      RoomId: parsed.RoomId ?? undefined,
      FullName: parsed.FullName ?? "",
      PhoneNumber: parsed.PhoneNumber ?? "",
      OrderBy: parsed.OrderBy ?? "",
      pageNumber: Number(parsed.pageNumber) || 1,
      pageSize: Number(parsed.pageSize) || pageSize,
      IsAscending:
        parsed.IsAscending === "true"
          ? true
          : parsed.IsAscending === "false"
          ? false
          : undefined,
      RoomStatusFilter: parsed.RoomStatusFilter
        ? Number(parsed.RoomStatusFilter)
        : undefined,
    };
  };

  const [filters, setFilters] = useState(getFiltersFromUrl());
  const [tempFilters, setTempFilters] = useState(filters);

  const [selectedTicket, setSelectedTicket] = useState(null);

  // برای تیکت‌ها
  const [lastTicketId, setLastTicketId] = useState(null);

  // برای پیام‌ها
  const [messages, setMessages] = useState([]);
  const [messagesLastId, setMessagesLastId] = useState(null); // برای beforeId
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [shouldAutoScrollBottom, setShouldAutoScrollBottom] = useState(false);
  const [isInitialMessagesLoading, setIsInitialMessagesLoading] =
    useState(false);
  const [newMessage, setNewMessage] = useState("");

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // ✅ کانتینر اسکرول پیام‌ها

  useSyncPageSize(filters, setFilters, pageSize);

  useEffect(() => {
    setMessages([]);
    setMessagesLastId(null);
    setHasMoreMessages(true);
    setIsLoadingMoreMessages(false);
    setNewMessage("");
    if (selectedTicket) {
      setShouldAutoScrollBottom(true);
    }
  }, [selectedTicket?.id]);

  const { data: tickets, refetch } = useQuery({
    queryKey: [
      "GetTickets",
      filters.pageNumber,
      filters.pageSize,
      filters?.RoomId,
      filters?.FullName,
      filters?.RoomStatusFilter,
      lastTicketId,
    ],
    queryFn: () =>
      GetTickets({
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        RoomId: filters?.RoomId ? filters?.RoomId : undefined,
        FullName: filters?.FullName ? filters?.FullName : undefined,
        RoomStatusFilter: filters?.RoomStatusFilter,
      }),
    select: (res) => res,
    retry: false,
    onSuccess: (data) => {
      if (!lastTicketId && data?.items?.length > 0) {
        setLastTicketId(data.items[0].id);
      }
    },
  });

  const {
    data: user,
    isLoading: loadingUser,
    error: errorUser,
  } = useQuery({
    queryKey: ["UserInfo"],
    queryFn: () => UserInfo(),
    select: (res) => res,
    retry: false,
  });

  useEffect(() => {
    if (!selectedTicket?.id) return;

    let cancelled = false;

    const fetchInitialMessages = async () => {
      try {
        setIsInitialMessagesLoading(true);

        const res = await GetMessages({
          roomId: selectedTicket.id,
          pageSize: MESSAGES_PAGE_SIZE,
        });

        if (cancelled) return;

        const items = res.items ?? [];
        setMessages(items);

        if (items.length) {
          setMessagesLastId(res?.lastId ?? items[0].id);
        } else {
          setMessagesLastId(null);
        }

        if (items.length < MESSAGES_PAGE_SIZE) {
          setHasMoreMessages(false);
        } else {
          setHasMoreMessages(true);
        }

        setShouldAutoScrollBottom(true);
      } finally {
        if (!cancelled) {
          setIsInitialMessagesLoading(false);
        }
      }
    };

    fetchInitialMessages();

    return () => {
      cancelled = true;
    };
  }, [selectedTicket?.id]);

  useEffect(() => {
    if (!shouldAutoScrollBottom) return;
    if (!messages.length) return;

    const timeout = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [messages, shouldAutoScrollBottom]);

  useEffect(() => {
    if (!selectedTicket?.id) return;
    if (!hasMoreMessages || isLoadingMoreMessages) return;
    if (!messages.length) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    if (el.scrollHeight <= el.clientHeight + 2) {
      loadOlderMessages();
    }
  }, [messages, selectedTicket?.id, hasMoreMessages, isLoadingMoreMessages]);

  const loadOlderMessages = async () => {
    if (
      !selectedTicket?.id ||
      !hasMoreMessages ||
      isLoadingMoreMessages ||
      !messagesLastId
    ) {
      return;
    }

    try {
      setIsLoadingMoreMessages(true);

      const res = await GetMessages({
        roomId: selectedTicket.id,
        pageSize: MESSAGES_PAGE_SIZE,
        beforeId: messagesLastId,
      });

      const older = res?.items ?? [];

      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev]);

        setMessagesLastId(res?.lastId ?? older[0].id);

        if (older.length < MESSAGES_PAGE_SIZE) {
          setHasMoreMessages(false);
        }
      } else {
        setHasMoreMessages(false);
      }
    } finally {
      setIsLoadingMoreMessages(false);
    }
  };

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop === 0) {
      loadOlderMessages();
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;
    if (!newMessage.trim()) return;

    const text = newMessage.trim();
    setNewMessage("");

    try {
      const res = await SendMessages({
        roomId: selectedTicket.id,
        message: text,
      });

      if (res?.errorCode && res.errorCode !== 0) {
        const err = new Error(res?.message || "خطا در ارسال پیام");

        toast.current?.show({
          severity: "error",
          summary: "خطا",
          detail: res?.message,
          life: 4000,
        });
        err.code = res.error;
        err.raw = res;
        throw err;
      }

      await refetch();

      const msgsRes = await GetMessages({
        roomId: selectedTicket.id,
        pageSize: MESSAGES_PAGE_SIZE,
      });

      const items = msgsRes.items ?? [];
      setMessages(items);

      if (items.length) {
        setMessagesLastId(msgsRes?.lastId ?? items[0].id);
      } else {
        setMessagesLastId(null);
      }

      if (items.length < MESSAGES_PAGE_SIZE) {
        setHasMoreMessages(false);
      } else {
        setHasMoreMessages(true);
      }

      setShouldAutoScrollBottom(true);
    } catch (err) {
      console.error("SendMessages error:", err);
      setNewMessage(text);
    }
  };

  const ROOM_STATUS_CONFIG = {
    0: {
      label: "پیام جدید",
      className: "bg-success text-white",
    },
    1: {
      label: "در حال بررسی",
      className: "bg-warning text-dark",
    },
    2: {
      label: "پاسخ داده شده",
      className: "bg-primary text-white",
    },
    3: {
      label: "حذف شده توسط کاربر",
      className: "bg-secondary text-light text-decoration-line-through",
    },
    4: {
      label: "بسته شده",
      className: "bg-light text-muted border",
    },
  };

  const columns = [
    {
      field: "id",
      header: "شناسه",
      style: { textAlign: "right" },
    },
    {
      field: "frontUserName",
      header: "کاربر",
      style: { textAlign: "right" },
    },
    {
      field: "subject",
      header: "عنوان",
      style: { textAlign: "right" },
    },
    {
      field: "frontUserPhoneNumber",
      header: "شماره تماس",
      style: { textAlign: "right" },
    },
    {
      field: "lastMessageTime",
      header: "تاریخ آخرین پیام",
      body: (rowData) => <PersianDate date={rowData.lastMessageTime} />,
      style: { textAlign: "right" },
    },
    {
      field: "roomStatus",
      header: "وضعیت",
      body: (rowData) => {
        const cfg = ROOM_STATUS_CONFIG[rowData?.roomStatus] || {
          label: "نامشخص",
          className: "bg-secondary text-white",
        };
        return (
          <span
            className={`badge px-2 py-1 rounded-pill ${
              cfg.className || "bg-secondary text-white"
            }`}
          >
            {cfg.label || "نامشخص"}
          </span>
        );
      },
      style: { textAlign: "right" },
    },
    {
      header: "بستن تیکت  ",
      body: (rowData) => {
        return (
          <div
            onClick={() => {
              ChangeRoomStatus({
                roomId: rowData?.id,
                roomStatusInput: 4,
              });
              refetch();
            }}
          >
            {(rowData?.roomStatus == 0 ||
              rowData?.roomStatus == 1 ||
              rowData?.roomStatus == 2) && (
              <CloseRedIconSvg
                style={{
                  cursor: "pointer",
                  width: "20px",
                  height: "20px",
                }}
              />
            )}
          </div>
        );
      },
      style: { textAlign: "right" },
    },
  ];

  const itemTemplate = (item) => (
    <div
      key={item.id}
      className="card shadow-sm mb-3 p-3"
      style={{
        backgroundColor: "#f9f9f9",
        border: "1px solid #eee",
        direction: "rtl",
      }}
    >
      <div className="d-flex justify-content-between mb-1">
        <strong>شناسه:</strong>
        <span>{item.id}</span>
      </div>
      <div className="d-flex justify-content-between mb-1">
        <strong>کاربر:</strong>
        <span>{item.frontUserName}</span>
      </div>
      <div className="d-flex justify-content-between mb-1">
        <strong>عنوان:</strong>
        <span>{item.subject}</span>
      </div>
      <div className="d-flex justify-content-between mb-1">
        <strong>شماره تماس:</strong>
        <span>{item.frontUserPhoneNumber}</span>
      </div>
    </div>
  );

  const orderByOptions = [
    { label: "پیام جدید", value: 0 },
    { label: "در حال بررسی", value: 1 },
    { label: "پاسخ داده شده", value: 2 },
    { label: "حذف شده توسط کاربر", value: 3 },
    { label: "بسته شده", value: 4 },
  ];

  const handleSearch = () => {
    setFilters(tempFilters);
    refetch();
  };

  return (
    <div className="custom-container mt-4">
      <Toast ref={toast} position="top-center" />

      <div className="row">
        <div className="col-6">
          <div className="card h-100 border-0 shadow-sm">
            <h5 className="card-header border-0 fw-bold">تیکت‌ها</h5>
            <div className="background-dataTable">
              <div className="row mt-2">
                <div className="col-12 col-xl-4 col-md-5 mt-md-0 mt-2">
                  <TextField
                    style={{ height: "40px" }}
                    value={tempFilters.RoomId || ""}
                    label={"شناسه"}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, RoomId: e.target.value })
                    }
                  />
                </div>
                <div className="col-12 col-xl-4 col-md-5 mt-md-0 mt-2">
                  <TextField
                    style={{ height: "40px" }}
                    value={tempFilters.FullName}
                    label={"نام"}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        FullName: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-12 col-xl-4 col-md-5  mt-md-0 mt-2">
                  <SelectCustom
                    name="RoomStatusFilter"
                    placeholder="مرتب کردن بر اساس"
                    isClearable={true}
                    isSearchable={false}
                    unstyled={true}
                    options={orderByOptions}
                    value={
                      orderByOptions.find(
                        (item) => item.value === tempFilters.RoomStatusFilter
                      ) || null
                    }
                    onChange={(e) => {
                      setTempFilters({
                        ...tempFilters,
                        RoomStatusFilter: e ? e.value : undefined,
                      });
                    }}
                  />
                </div>
                <div className="col-12 col-xl-4 col-md-5  mt-2">
                  <button
                    style={{
                      color: "#ffff",
                      background: "#86a0ff",
                      border: "0px",
                      width: "108px",
                      height: "39px",
                      borderRadius: "8px",
                      padding: "2px",
                    }}
                    onClick={handleSearch}
                  >
                    جستجو
                  </button>
                </div>
              </div>
              <div className="card-body pt-0">
                <ResponsiveDataList
                  data={tickets?.items || []}
                  columns={columns}
                  itemTemplate={itemTemplate}
                  dataViewHeader={<></>}
                  selection={selectedTicket}
                  onSelectionChange={(e) => {
                    setSelectedTicket(e.value);
                  }}
                  dataKey="id"
                  className="farsi-monospace-digit"
                  rows={filters.pageSize}
                  pageNumber={filters.pageNumber}
                  totalCount={tickets?.totalCount}
                  enableSelection={true}
                  selectOnCardClick={true}
                  selectionMode="single"
                  onPageChange={({ pageNumber }) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber,
                    }));
                  }}
                  typeFilter={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-6">
          <div className="card d-flex flex-column border-0 shadow-sm">
            {/* هدر چت */}
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{
                background:
                  "linear-gradient(135deg, #4e8eff 0%, #6fb1ff 50%, #194daf 100%)",
                color: "#fff",
              }}
            >
              {selectedTicket ? (
                <>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      {selectedTicket?.frontUserName
                        ?.toString()
                        ?.trim()
                        ?.charAt(0) || "؟"}
                    </div>
                    <Link
                      className="mx-1 cursor-pointer"
                      to={`/user-information?userId=${selectedTicket?.fromUserId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div className="d-flex flex-column">
                        <span className="fw-semibold">
                          {selectedTicket?.frontUserName}
                        </span>
                        <small className="opacity-75">
                          {selectedTicket?.frontUserPhoneNumber}
                        </small>
                      </div>
                    </Link>
                  </div>
                  <div className="d-flex flex-column">
                    <span className="small opacity-75 text-end">
                      {selectedTicket.subject}
                    </span>
                    <small className="opacity-75">
                      تیکت #{selectedTicket.id}
                    </small>
                  </div>
                </>
              ) : (
                <span>یک تیکت را از لیست انتخاب کنید</span>
              )}
            </div>

            <div
              ref={messagesContainerRef}
              className="card-body overflow-auto"
              style={{
                height: "744px",
                direction: "rtl",
                background:
                  "linear-gradient(180deg, #333333 0%, #272727 40%, #0f0f0f 100%)",
                paddingTop: "12px",
                paddingBottom: "12px",
              }}
              onScroll={handleMessagesScroll}
            >
              {isInitialMessagesLoading &&
                !messages.length &&
                selectedTicket && (
                  <p className="text-center  mt-3">
                    در حال بارگذاری پیام‌ها...
                  </p>
                )}

              {isLoadingMoreMessages && (
                <p className="text-center small  my-2">
                  در حال بارگذاری پیام‌های قبلی...
                </p>
              )}

              {!isInitialMessagesLoading &&
                !messages.length &&
                selectedTicket && (
                  <p className="text-center  mt-3">
                    هیچ پیامی برای این تیکت ثبت نشده است.
                  </p>
                )}

              <div
                className="d-flex flex-column"
                style={{ gap: "6px", paddingInline: "8px" }}
              >
                {messages.map((msg) => {
                  const isSelf = user?.id == String(msg?.senderUserId);
                  const alignmentClass = isSelf
                    ? "justify-content-start"
                    : "justify-content-end";

                  const bubbleClass = isSelf
                    ? "bg-white"
                    : "bg-primary text-white";

                  const bubbleStyle = {
                    maxWidth: "75%",
                    borderRadius: "18px",
                    borderTopRightRadius: isSelf ? "4px" : "18px",
                    borderTopLeftRadius: isSelf ? "18px" : "4px",
                    fontSize: "0.9rem",
                    boxShadow: isSelf
                      ? "0 1px 3px rgba(0,0,0,0.1)"
                      : "0 1px 2px rgba(0,0,0,0.08)",
                  };

                  return (
                    <div
                      key={msg.id}
                      className={`d-flex ${alignmentClass}`}
                      style={{ direction: "rtl" }}
                    >
                      <div
                        className={`p-2 px-3 ${bubbleClass}`}
                        style={bubbleStyle}
                      >
                        <div>{msg.content}</div>
                        <div
                          className="small mt-1 text-end"
                          style={{
                            opacity: 0.7,
                            fontSize: "0.75rem",
                          }}
                        >
                          <PersianDate date={msg.creationTime} />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef} />

                {!hasMoreMessages && messages.length > 0 && (
                  <p className="text-center small  my-2 ">
                    پیام قدیمی‌تری وجود ندارد.
                  </p>
                )}
              </div>
            </div>

            <div
              className="card-footer  border-top"
              style={{ paddingTop: 10, paddingBottom: 10 }}
            >
              <form onSubmit={handleSendMessage}>
                <div className="d-flex align-items-center gap-2">
                  <textarea
                    className="form-control rounded-pill"
                    style={{
                      direction: "rtl",
                      fontSize: "0.9rem",
                      resize: "none",
                      height: "40px",
                      paddingTop: "8px",
                    }}
                    placeholder={
                      selectedTicket
                        ? "پیام خود را بنویسید..."
                        : "ابتدا یک تیکت را انتخاب کنید"
                    }
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={!selectedTicket}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                    disabled={!selectedTicket || !newMessage.trim()}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        transform: "rotate(-40deg)",
                        fontSize: "1.1rem",
                      }}
                    >
                      ➤
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tiket;
